import { Box, TextField, CircularProgress } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from "../../../components/Cards/Card";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from "react";
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BASE_URL } from "../../../components/BaseUrl";
import { setProfileUpdateToken } from "../../../Redux/Slices/AuthSlice";
import { useTranslation } from "react-i18next"
import { useLocation } from 'react-use';
import { apiPost, apiGet } from '../../../utils/apiService';

const OTPScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
    const location = useLocation(); // 👈 get state from navigation

  const otpFromRedux = useSelector((state) => state.auth.otpData?.otp);
  const userId = useSelector((state) => state.auth.otpData?.userId);
    // const email = useSelector((state) => state.auth.user?.email);
 const reduxEmail = useSelector((state) => state.auth.user?.email)

  // 👇 Prefer email from location.state if present, else fallback to Redux
  const email = location.state?.email || reduxEmail;
  const [otpError, setOtpError] = useState(false); // Error state for OTP

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(15); // Start timer at 60
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);
const [isResending, setIsResending] = useState(false);

  // Start the timer on mount
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (otpError && value) {
      setOtpError(false);
    }
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  // Handle backspace key to navigate to previous input
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handlePaste = (e) => {
  e.preventDefault();
  const pasteData = e.clipboardData.getData("text").trim();
  
  // Only accept digits
  if (!/^\d{1,6}$/.test(pasteData)) return;

  // Split digits and fill inputs
  const newOtp = pasteData.split("").slice(0, 6);
  setOtp(newOtp);

  // Remove error if previously shown
  if (otpError) setOtpError(false);

  // Optionally move focus to last filled field
  const lastIndex = newOtp.length - 1;
  if (lastIndex >= 0) inputRefs.current[lastIndex]?.focus();
};

  // Resend OTP
  // const handleResendOtp = async () => {
  //   setTimer(60); // Reset the timer
  // };
const handleResendOtp = async () => {
  setIsResending(true);
  setOtp(["", "", "", "", "", ""]); // ✅ clear inputs
  setOtpError(false);
  
  try {
    const data = await apiPost(`${BASE_URL}/company-admins/resend-verification-otp`, {
      email: email, // Send email from redux
    });

    if (data.ok && !data.error) {
      // On success, show success toast with localized message
      toast.success(data.message || "OTP Resent Successfully!");
      setTimer(15); // ✅ restart the timer after success
    } else {
      // On failure, show error toast with localized message
      toast.error(data.message || "Failed to resend OTP.");
    }
  } catch (error) {
    toast.error(error.message || "An error occurred while resending OTP.");
  } finally {
    setIsResending(false); // ✅ stop loader
  }
};

  // Verify OTP
  const handleVerify = async () => {
    const fullOtp = otp.join("");
    if (fullOtp.length !== 6 || !userId) {
toast.error(t("invalidOtptext"));
      setOtpError(true);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const url = `${BASE_URL}/public/company-admin/verify-invite?userId=${userId}&token=${fullOtp}`;
      const data = await apiGet(url);

      if (!data.ok || data.error) {
        toast.error(data.message || "OTP verification failed");
        setOtpError(true);
        return;
      }

      toast.success(data.message || "OTP Verified Successfully");

      const profileToken = data.data?.profileUpdateToken;
      if (profileToken) {
        dispatch(setProfileUpdateToken(profileToken));
        localStorage.setItem("profileUpdateToken", profileToken);
      }

      setTimeout(() => {
        navigate("/create-profile");
      }, 1000);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#EFF4F8", pt: isMobile ? 3 : 5, minHeight: "100vh" }}>
  
      <Box sx={{ pt: isMobile ? 5 : 10, pb: isMobile ? 5 : 20, px: 2, display: 'flex', justifyContent: 'center' }}>
        <BaseCard maxWidth={isMobile ? '100%' : 380} sx={{ width: isMobile ? '100%' : 'auto', px: isMobile ? 1 : 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main, fontSize: isMobile ? "28px" : "40px" }}>
                {t("otp.title")}
              </CustomText>
              <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 2 }}>
                {t("otp.subtitle")}
              </CustomText>
            </Box>

            {/* OTP Inputs */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={el => inputRefs.current[index] = el}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                     onPaste={handlePaste} 
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '24px',
                      width: '45px',
                      height: '30px',
                      borderRadius: '12px',
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: otpError ? 'red' : (digit ? theme.palette.primary.main : '#ccc'),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: otpError ? 'red' : theme.palette.primary.main,
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              ))}
            </Box>

     
            <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, textAlign: 'center' }}>
  {t("otp.resendText")}{" "}
  <span
    onClick={timer === 0 && !isResending ? handleResendOtp : undefined}
    style={{
      color: "#0066FF",
      fontWeight: 600,
      cursor: timer === 0 && !isResending ? "pointer" : "default",
    }}
  >
    {isResending ? (
      <CircularProgress size={16} sx={{ color: "#0066FF" }} />
    ) : timer > 0 ? `${timer}s` : t("otp.resendButton")}
  </span>
</CustomText>

            <CustomButton
              label={
                isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    <span>{t("otp.verifying")}</span>
                  </Box>
                ) : (
                  <span>{t("otp.verifyButton")}</span>
                )
              }
              onClick={handleVerify}
              fullWidth
              sx={{ mt: 2 }}
            />
          </Box>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default OTPScreen;










